import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Loader2, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { UserAvatar, RankBadge } from './RankBadge';
import api from '../lib/api';
import { toast } from 'sonner';

function formatRelative(iso) {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (Number.isNaN(diff)) return '';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Shows a list of who's recently viewed the current user's profile.
 * Powered by GET /api/users/me/visitors (last 30 days, top 20 distinct viewers).
 */
export default function VisitorsDialog({ open, onOpenChange }) {
  const [data, setData] = useState({ count: 0, recent: [], window_days: 30 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.get('/users/me/visitors')
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) toast.error('Could not load visitors'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md" data-testid="visitors-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Eye size={16} className="text-amber-300" /> Profile visitors
          </DialogTitle>
          <DialogDescription className="text-white/55">
            {data.count > 0
              ? <><span className="text-amber-300 font-semibold">{data.count}</span> traveler{data.count === 1 ? '' : 's'} checked you out in the last {data.window_days} days.</>
              : 'No visitors yet — keep chatting and creating to get on more travelers\u2019 radars.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-white/45 text-sm flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : data.recent.length === 0 ? (
          <div className="py-8 text-center text-white/40 text-sm" data-testid="visitors-empty">
            Nobody&apos;s peeked at your profile recently.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto scroll-hide -mx-2 px-2">
            {data.recent.map(v => (
              <Link
                key={v.viewer_id}
                to={`/app/users/${v.viewer_id}`}
                onClick={() => onOpenChange(false)}
                data-testid={`visitor-${v.viewer_id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/3 border border-white/8 hover:bg-white/6 transition"
              >
                <UserAvatar user={v} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-sm truncate">{v.displayName}</span>
                    <RankBadge rank={v.rank || 1} />
                  </div>
                  <div className="text-[11px] text-white/45">{formatRelative(v.viewed_at)}</div>
                </div>
                <span className="text-[11px] text-cyan-300/80 hover:text-cyan-200 flex items-center gap-0.5 shrink-0 pr-0.5">
                  View <ChevronRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
